"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import {
  EDIT_CHAT_SUGGESTIONS,
  EDIT_WITH_CHAT_HREF,
  getHeroProducts,
  type HeroProductId,
} from "./heroLabData";

const MODES: HeroProductId[] = ["edit-chat", "headshots", "photoshoot", "photo-editor"];

/** Option A - Aragon-style "What will you create?" with a mode switcher and a prompt box. */
export default function HeroPromptStudio() {
  const products = useMemo(() => getHeroProducts(), []);
  const router = useRouter();
  const [mode, setMode] = useState<HeroProductId>("edit-chat");
  const [prompt, setPrompt] = useState("");
  const active = products[mode];

  return (
    <section className="relative mb-8 flex flex-col items-center text-center md:mb-10">
      <div
        className="pointer-events-none absolute left-1/2 top-16 -z-10 h-64 w-[min(90vw,760px)] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl"
        aria-hidden
      />

      <h1
        className="text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl"
        style={{ fontFamily: "var(--font-merriweather)" }}
      >
        What will you <span className="text-primary">create</span>?
      </h1>
      <p className="mt-2 text-base text-gray-500 md:text-lg">
        Headshots, photoshoots, and edits - all from one selfie.
      </p>

      <div
        className="mt-5 flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-primary/15 bg-white p-1.5 px-2 shadow-sm"
        style={{ scrollbarWidth: "none" }}
      >
        {MODES.map((id) => {
          const p = products[id];
          const on = id === mode;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setMode(id)}
              className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full py-1 pl-1 pr-3.5 text-sm font-semibold transition-colors ${
                on ? "bg-primary text-white" : "text-gray-700 hover:bg-primary/5 hover:text-primary"
              }`}
            >
              {p.images[0] ? (
                <img
                  src={p.images[0]}
                  alt=""
                  className={`h-7 w-7 rounded-full object-cover object-top ${on ? "ring-2 ring-white/70" : ""}`}
                  draggable={false}
                />
              ) : null}
              {p.label}
              {p.badge === "NEW" ? (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                    on ? "bg-white text-primary" : "bg-primary/10 text-primary"
                  }`}
                >
                  NEW
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="mt-5 w-full max-w-3xl rounded-3xl border border-primary/20 bg-white p-3 text-left shadow-[0_12px_40px_rgba(89,86,233,0.14)] transition focus-within:border-primary/50 md:p-4">
        {mode === "edit-chat" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              router.push(EDIT_WITH_CHAT_HREF);
            }}
          >
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => router.push(EDIT_WITH_CHAT_HREF)}
                className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 text-primary transition hover:border-primary hover:bg-primary/10"
                aria-label="Upload photo"
              >
                <span className="text-xl leading-none">+</span>
                <span className="mt-0.5 text-[10px] font-semibold">Photo</span>
              </button>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={2}
                placeholder="What should change? e.g. remove my glasses and blur the background"
                className="min-h-16 flex-1 resize-none border-0 bg-transparent p-1 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
              />
            </div>
            <div className="mt-3 flex items-center gap-3">
              <div
                className="flex min-w-0 flex-1 gap-2 overflow-x-auto pr-6 [mask-image:linear-gradient(to_right,black_85%,transparent)]"
                style={{ scrollbarWidth: "none" }}
              >
                {EDIT_CHAT_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setPrompt(s)}
                    className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      prompt === s
                        ? "border-primary bg-primary text-white"
                        : "border-primary/15 bg-primary/5 text-primary hover:bg-primary/10"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <button
                type="submit"
                className="shrink-0 whitespace-nowrap rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 hover:shadow-md"
              >
                Edit photo →
              </button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex -space-x-3">
              {active.images.slice(0, 4).map((src) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  className="h-16 w-16 rounded-2xl border-2 border-white object-cover object-top shadow-sm"
                />
              ))}
            </div>
            <div className="flex-1">
              <p className="text-base font-bold text-gray-900">{active.label}</p>
              <p className="text-sm text-gray-500">{active.blurb}</p>
            </div>
            <Link
              href={active.href}
              className="rounded-full bg-primary px-5 py-2.5 text-center text-sm font-semibold text-white shadow-sm transition hover:brightness-110 hover:shadow-md"
            >
              Start {active.label} →
            </Link>
          </div>
        )}
      </div>

      <p className="mt-3 text-sm text-gray-600">
        <span className="font-semibold text-primary">New:</span> Edit your photo again and again - no AI artifacts or blur even after 100+ edits.
      </p>
    </section>
  );
}
